import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CsIconModule } from '@components/icon';
import { ImageCardModule } from '../../user/user-view/user-assistant/user-chat/components/image-card/image-card.module';
import { AudioModule } from '../../user/user-view/user-assistant/user-chat/components/audio/audio.module';
import { ArticleModule } from '../../user/user-view/user-assistant/user-chat/components/article/article.module';
import { RebrandedSingleSelectorComponent } from './rebranded-single-selector/rebranded-single-selector.component';
import { AppConfig } from '../../app.config';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { TimeService } from '@services/time-service';
import { isPhotoQuestion } from '@services/chat-service/chat-message.utils';
import { UserTable } from '@cureskin/api-client/src/table/user';
import { ChatMessageData, ChatInput, OptionSelectedEvent } from './rebranded-single-selector/rebranded-single-selector.types';

@Component({
  selector: 'chat-message',
  templateUrl: './chat-message.component.html',
  standalone: true,
  host: { class: 'tw-block tw-pt-6' },
  imports: [
    CommonModule,
    CsIconModule,
    ImageCardModule,
    AudioModule,
    ArticleModule,
    RebrandedSingleSelectorComponent,
  ],
})
export class ChatMessageComponent {
  @Input() chat: ChatMessageData;
  @Input() isLast: boolean = false;
  @Input() isInternalUser: boolean = false;
  @Input() allowAnimation: boolean = false;
  @Input() selectedChatType: Parse.Object;
  @Input() user: UserTable;
  @Input() allocatedDoctor: UserTable;
  @Input() isLatestMessage: boolean = false;
  @Input() botReplyInProcess: boolean = false;

  @Output() assistantAction = new EventEmitter<OptionSelectedEvent>();
  @Output() imageCardClick = new EventEmitter<ChatMessageData>();
  @Output() imageModalOpen = new EventEmitter<ChatMessageData>();
  @Output() imagesExpand = new EventEmitter<ChatMessageData>();
  @Output() bothSideFaceImageClick = new EventEmitter<string>();

  constructor(
    public appConfig: AppConfig,
    private readonly commonUtilityHelper: CommonUtilityHelper,
    private readonly timeService: TimeService,
  ) {}

  hasImageInput(chat: ChatMessageData): boolean {
    return (chat?.get('Inputs') || []).some((i: ChatInput) => i.type === 'IMAGE');
  }

  get isUserImageMessage(): boolean {
    return this.chat.get('Mode') === this.appConfig.Shared.Assistant.Mode.IMAGE_CLUBBED || this.chat.get('Mode') === this.appConfig.Shared.Assistant.Mode.REQUEST_IMAGE_RESPONSE_BOTH_SIDE_FACE
  }

  get isClosedTicket(): boolean {
    return (
      this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.Completed ||
      this.selectedChatType?.get('status') === this.appConfig.Shared.Ticket.status.RequestClosure
    );
  }

  get isRightBubble(): boolean {
    return (this.isInternalUser && this.chat?.get('Owner') !== 'USER') ||
           (!this.isInternalUser && this.chat?.get('Owner') === 'USER');
  }

  get bubbleClasses(): Record<string, boolean> {
    return {
      'tw-relative tw-rounded-t-2 tw-rounded-br-2 tw-origin-left': !this.isRightBubble,
      'tw-bg-teal-150 tw-float-right tw-relative tw-origin-right tw-text-black tw-rounded-tl-2 tw-rounded-tr-2': this.isRightBubble,
      'tw-mb-8': this.isLast,
      'tw-rounded-bl-2': this.isRightBubble || ((this.hasActionCta || (this.hasTreeCta && !this.hasImageInput(this.chat))) && !this.isRightBubble),
      'tw-shadow-z1': (!this.hasTreeCta && !this.hasActionCta) || this.hasImageInput(this.chat) || this.selectedChatType?.className === 'SupportTicket',
      'tw-rounded-bl-[0px]': (this.selectedChatType?.className === 'SupportTicket' && !this.isRightBubble),
    };
  }

  get showBubble(): boolean {
    return this.chat?.get('Mode') !== this.appConfig.Shared.Assistant.Mode.SMS_COPY &&
           this.chat?.get('Mode') !== this.appConfig.Shared.Assistant.Mode.REQUEST_IMAGE_RESPONSE_BOTH_SIDE_FACE;
  }

  get showTimestamp(): boolean {
    return !this.appConfig.Shared.Assistant.MediaTypeModes.includes(this.chat?.get('Mode'));
  }

  get timestampLabel(): string {
    const owner = this.chat?.get('Owner');
    const questionId = this.chat?.get('questionId');
    const isAiBot = owner === 'BOT' &&
      (questionId === 'lLMInstructionAssistantMessage1' || questionId === 'lLMInstructionAssistantMessage2');
    if (isAiBot) return 'AI bot';
    if (this.isInternalUser) return this.chat?.get('OperatorName') || (owner === 'USER' ? '' : owner);
    return owner === 'USER' ? '' : owner;
  }

  get hasTreeCta(): boolean {
    return (
      !this.hasActionCta &&
      this.isLatestMessage &&
      !this.botReplyInProcess &&
      !!this.chat?.get('Inputs')?.length
    );
  }

  get hasActionCta(): boolean {
    const photoGated = isPhotoQuestion(this.chat, this.selectedChatType?.className);
    return (
      (!photoGated || this.isLatestMessage) &&
      this.chat?.get('Owner') !== 'USER' &&
      !!this.chat?.get('Inputs')?.length &&
      this.appConfig.Shared.Assistant.RequestImageModes.includes(this.chat?.get('Mode'))
    );
  }

  getImageTypeMappingFace(side: string): string {
    const mapping = this.chat.get('imageTypeMapping') as Record<string, string> | undefined;
    return mapping?.[side] || '';
  }

  convertTextToHtml(text: string): string {
    return this.commonUtilityHelper.convertTextToHtml(text);
  }

  formatTime(date: string | Date): string {
    const dateString = date instanceof Date ? date.toISOString() : (date || new Date().toISOString());
    return this.timeService.formatChatTime(dateString);
  }
}
